import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';

const TimelineView = ({
  tasks,
  currentLang,
  onDelete,
  onCopy,
  onEdit,
  onStatusChange,
  deletingTasks,
  getStatusText,
  getStatusColor,
  isTaskEditor,
  getSiteName,
  isTaskOverdue,
  getDaysOverdue,
  onMessagesClick,
  onFilesClick,
  getSiteColor
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('week'); // 'month', 'week'

  // --- состояния для дропдауна статусов ---
  const [openStatusDropdowns, setOpenStatusDropdowns] = useState(new Set());
  const [updatingStatus, setUpdatingStatus] = useState(new Set());

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      setUpdatingStatus(prev => new Set(prev).add(taskId));
      await onStatusChange(taskId, newStatus);
    } finally {
      setUpdatingStatus(prev => {
        const newSet = new Set(prev);
        newSet.delete(taskId);
        return newSet;
      });
    }
  };
  const closeStatusDropdown = (taskId) => {
    setOpenStatusDropdowns(prev => {
      const newSet = new Set(prev);
      newSet.delete(taskId);
      return newSet;
    });
  };

  // Получение первого и последнего дня месяца
  const getMonthRange = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // Начинаем с понедельника недели, в которой находится первый день месяца
    const startDate = new Date(firstDay);
    const dayOfWeek = firstDay.getDay();
    const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // 0 = воскресенье
    startDate.setDate(firstDay.getDate() - mondayOffset);
    
    // Заканчиваем воскресеньем недели, в которой находится последний день месяца
    const endDate = new Date(lastDay);
    const endDayOfWeek = lastDay.getDay();
    const sundayOffset = endDayOfWeek === 0 ? 0 : 7 - endDayOfWeek;
    endDate.setDate(lastDay.getDate() + sundayOffset);
    
    return { startDate, endDate };
  };

  // Получение диапазона недели
  const getWeekRange = (date) => {
    const startDate = new Date(date);
    const dayOfWeek = date.getDay();
    const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    startDate.setDate(date.getDate() - mondayOffset);
    
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 6);
    
    return { startDate, endDate };
  };

  // Получение диапазона дат для отображения
  const getDisplayRange = () => {
    return viewMode === 'month' ? getMonthRange(currentDate) : getWeekRange(currentDate);
  };

  // Генерация календарной сетки
  const calendarGrid = useMemo(() => {
    const { startDate, endDate } = getDisplayRange();
    const grid = [];
    const current = new Date(startDate);
    
    while (current <= endDate) {
      grid.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return grid;
  }, [currentDate, viewMode]);

  // Группировка задач по дням
  const tasksByDay = useMemo(() => {
    const grouped = {};
    
    tasks.forEach(task => {
      if (!task.d_start || !task.d_end) return;
      
      const startDate = new Date(task.d_start);
      const endDate = new Date(task.d_end);
      
      // Добавляем задачу во все дни от начала до конца
      const current = new Date(startDate);
      while (current <= endDate) {
        const dateKey = current.toISOString().split('T')[0];
        if (!grouped[dateKey]) {
          grouped[dateKey] = [];
        }
        grouped[dateKey].push(task);
        current.setDate(current.getDate() + 1);
      }
    });
    
    return grouped;
  }, [tasks]);

  // Навигация по календарю
  const goToPrevious = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setDate(newDate.getDate() - 7);
    }
    setCurrentDate(newDate);
  };

  const goToNext = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() + 1);
    } else {
      newDate.setDate(newDate.getDate() + 7);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Форматирование даты
  const formatDate = (date) => {
    if (!date) return '-';
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return '-';
    
    return dateObj.toLocaleDateString('ru-RU', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Форматирование короткой даты
  const formatShortDate = (date) => {
    if (!date) return '-';
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return '-';
    
    return dateObj.toLocaleDateString('ru-RU', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Очистка HTML тегов
  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // Получение цвета для задачи
  const getTaskColor = (task) => {
    if (isTaskOverdue(task)) return 'border-red-500';
    
    switch (task.status) {
      case 0: return 'border-yellow-500';
      case 1: return 'border-blue-500';
      case 2: return 'border-green-500';
      case 99: return 'border-gray-500';
      default: return 'border-gray-400';
    }
  };

  // Получение высоты для задачи (в зависимости от количества задач в дне)
  const getTaskHeight = (dayTasks, taskIndex) => {
    if (!dayTasks || dayTasks.length === 0) return 20;
    
    if (viewMode === 'week') {
      // В недельном режиме даем больше места для задач
      const baseHeight = 40; // Увеличена базовая высота
      const maxHeight = 250; // Больше места в недельном режиме
      const taskHeight = Math.min(maxHeight / dayTasks.length, baseHeight);
      return Math.max(taskHeight, 50); // Минимальная высота 32px в недельном режиме
    } else {
      // В месячном режиме компактное отображение
      const baseHeight =20;
      const maxHeight = 80;
      const taskHeight = Math.min(maxHeight / dayTasks.length, baseHeight);
      return Math.max(taskHeight, 30); // Минимальная высота 16px
    }
  };

  // Проверка, является ли дата сегодняшней
  const isToday = (date) => {
    if (!date) return false;
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return false;
    
    const today = new Date();
    return dateObj.toDateString() === today.toDateString();
  };

  // Проверка, является ли дата в текущем месяце
  const isCurrentMonth = (date) => {
    if (!date) return false;
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return false;
    
    const current = new Date(currentDate);
    return dateObj.getMonth() === current.getMonth() && dateObj.getFullYear() === current.getFullYear();
  };

  // Названия дней недели
  const weekDays = currentLang === 'ru' 
    ? ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']
    : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  // --- StatusDropdown (скопировано из tasks/index.jsx) ---
  const StatusDropdown = ({ task, openStatusDropdowns, updatingStatus, handleStatusChange, closeStatusDropdown, currentLang }) => {
    const isOpen = openStatusDropdowns.has(task.id);
    const isUpdating = updatingStatus.has(task.id);

    const statusOptions = [
      { value: 0, label: currentLang === 'ru' ? 'Ожидает' : 'Pending', color: 'yellow' },
      { value: 1, label: currentLang === 'ru' ? 'В работе' : 'In Progress', color: 'blue' },
      { value: 2, label: currentLang === 'ru' ? 'Завершена' : 'Completed', color: 'green' },
      { value: 99, label: currentLang === 'ru' ? 'Отменена' : 'Cancelled', color: 'red' }
    ];

    const currentStatus = statusOptions.find(option => option.value === task.status);

    const getStatusColorClasses = (color) => {
      const colorMap = {
        yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-200',
        blue: 'bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200',
        green: 'bg-green-100 text-green-800 border-green-200 hover:bg-green-200',
        red: 'bg-red-100 text-red-800 border-red-200 hover:bg-red-200'
      };
      return colorMap[color] || 'bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200';
    };

    return (
      <div className="status-dropdown relative">
        <button
          onClick={() => {
            const newSet = new Set(openStatusDropdowns);
            if (isOpen) newSet.delete(task.id); else newSet.add(task.id);
            setOpenStatusDropdowns(newSet);
          }}
          disabled={isUpdating}
          className={`inline-flex items-center px-3 py-1 text-xs font-bold rounded-full border-2 transition-all duration-200 ${
            isUpdating 
              ? 'opacity-50 cursor-not-allowed' 
              : 'cursor-pointer hover:shadow-md hover:scale-105'
          } ${getStatusColorClasses(currentStatus.color)}`}
        >
          {isUpdating ? (
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div>
              <span>{currentStatus.label}</span>
            </div>
          ) : (
            <>
              <span>{currentStatus.label}</span>
              <svg 
                className={`w-3 h-3 ml-1 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </>
          )}
        </button>

        {isOpen && !isUpdating && (
          <div className="absolute z-50 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 transform -translate-x-1/2 left-1/2 flex flex-col">
            {statusOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => {
                  handleStatusChange(task.id, option.value);
                  closeStatusDropdown(task.id);
                }}
                className={`w-full text-left px-4 py-2 text-sm transition-colors duration-150 ${
                  option.value === task.status
                    ? `${getStatusColorClasses(option.color)} font-semibold`
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <div className={`w-2 h-2 rounded-full ${
                    option.color === 'yellow' ? 'bg-yellow-400' :
                    option.color === 'blue' ? 'bg-blue-400' :
                    option.color === 'green' ? 'bg-green-400' :
                    option.color === 'red' ? 'bg-red-400' : 'bg-gray-400'
                  }`}></div>
                  <span>{option.label}</span>
                  {option.value === task.status && (
                    <svg className="w-4 h-4 ml-auto text-current" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Контролы календаря */}
      <div className="bg-white rounded-lg shadow-md p-4 dark:bg-navy-700 dark:text-white dark:border-white/10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={goToPrevious}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
                         <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
               {currentDate.toLocaleDateString('ru-RU', { 
                 year: 'numeric', 
                 month: 'long' 
               })}
             </h2>
            
            <button
              onClick={goToNext}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
          
          <div className="flex items-center space-x-4">
            <button
              onClick={goToToday}
              className="px-4 py-2 bg-brand-500 text-white rounded-md hover:bg-brand-600 text-sm"
            >
              {currentLang === 'ru' ? 'Сегодня' : 'Today'}
            </button>
            
                         <div className="flex bg-gray-200 rounded-lg p-1">
               <button
                 onClick={() => setViewMode('week')}
                 className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                   viewMode === 'week' 
                     ? 'bg-white text-gray-900 shadow-sm' 
                     : 'text-gray-600 hover:text-gray-900'
                 }`}
               >
                 {currentLang === 'ru' ? 'Неделя' : 'Week'}
               </button>
               <button
                 onClick={() => setViewMode('month')}
                 className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                   viewMode === 'month' 
                     ? 'bg-white text-gray-900 shadow-sm' 
                     : 'text-gray-600 hover:text-gray-900'
                 }`}
               >
                 {currentLang === 'ru' ? 'Месяц' : 'Month'}
               </button>
             </div>
          </div>
        </div>
      </div>

             {/* Календарная сетка */}
       <div className="bg-white rounded-lg shadow-md overflow-visible dark:bg-navy-700 dark:text-white dark:border-white/10">
        {/* Заголовки дней недели */}
        <div className="grid grid-cols-7 bg-gray-50 dark:bg-navy-600">
          {weekDays.map((day, index) => (
            <div key={index} className="p-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300 border-r border-gray-200 dark:border-white/10 last:border-r-0">
              {day}
            </div>
          ))}
        </div>

        {/* Календарные дни */}
        <div className="grid grid-cols-7">
          {calendarGrid.map((date, index) => {
            const dateKey = date.toISOString().split('T')[0];
            const dayTasks = tasksByDay[dateKey] || [];
            const isCurrentMonthDay = isCurrentMonth(date);
            const isTodayDay = isToday(date);
            
            return (
                               <div
                   key={index}
                   className={`min-h-[${viewMode === 'week' ? '300px' : '120px'}] border-r border-b border-gray-200 dark:border-white/10 last:border-r-0 relative overflow-visible ${
                     !isCurrentMonthDay ? 'bg-gray-50 dark:bg-gray-800' : ''
                   } ${isTodayDay ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                 >
                                 {/* Номер дня */}
                 <div className={`p-2 text-sm flex items-center justify-between ${
                   isTodayDay 
                     ? 'font-bold text-blue-600 dark:text-blue-400' 
                     : isCurrentMonthDay 
                       ? 'text-gray-900 dark:text-white' 
                       : 'text-gray-400 dark:text-gray-500'
                 }`}>
                   <span>{date.getDate()}</span>
                   {viewMode === 'week' && dayTasks.length > 0 && (
                     <span className="text-xs bg-brand-100 text-brand-800 dark:bg-brand-900 dark:text-brand-200 px-1 rounded-full">
                       {dayTasks.length}
                     </span>
                   )}
                 </div>

                                 {/* Задачи */}
                 <div className="px-2 pb-2 space-y-1">
                                      {dayTasks.slice(0, viewMode === 'week' ? dayTasks.length : 3).map((task, taskIndex) => {
                     // Определяем позицию тултипа в зависимости от дня недели
                     const dayOfWeek = date.getDay();
                     const isFirstDay = dayOfWeek === 1; // Понедельник
                     const isLastDay = dayOfWeek === 0; // Воскресенье
                     
                     // Определяем, нужно ли показывать тултип сверху или снизу
                     // Всегда показываем сверху для лучшей видимости
                     const showTooltipAbove = true;
                     
                     return (
                       <div
                         key={`${task.id}-${dateKey}`}
                         className={`bg-white dark:bg-gray-800 border-l-8 ${getTaskColor(task)} text-gray-900 dark:text-white text-xs p-2 rounded cursor-pointer hover:shadow-md transition-all duration-200 relative group`}
                         style={{ height: `${getTaskHeight(dayTasks, taskIndex)}px` }}
                         title={`${task.name} (${getStatusText(task.status)})`}
                         onMouseEnter={(e) => {
                           const tooltip = e.currentTarget.querySelector('.task-tooltip');
                           if (tooltip) {
                             const rect = e.currentTarget.getBoundingClientRect();
                             const tooltipRect = tooltip.getBoundingClientRect();
                             
                             // Позиционируем тултип выше элемента
                             let top = rect.top - tooltipRect.height - 12;
                             let left = rect.left + rect.width / 2 - tooltipRect.width / 2;
                             
                             // Проверяем, не выходит ли тултип за левый край экрана
                             if (left < 10) left = 10;
                             
                             // Проверяем, не выходит ли тултип за правый край экрана
                             if (left + tooltipRect.width > window.innerWidth - 10) {
                               left = window.innerWidth - tooltipRect.width - 10;
                             }
                             
                             // Проверяем, не выходит ли тултип за верхний край экрана
                             if (top < 10) {
                               // Если выходит за верхний край, показываем снизу
                               top = rect.bottom + 12;
                             }
                             
                             tooltip.style.top = `${top}px`;
                             tooltip.style.left = `${left}px`;
                           }
                         }}
                       >
                                                 <div className="space-y-1">
                           <div className="truncate font-medium">
                             {viewMode === 'week' 
                               ? (task.name.length > 25 ? task.name.substring(0, 25) + '...' : task.name)
                               : (task.name.length > 15 ? task.name.substring(0, 15) + '...' : task.name)
                             }
                           </div>
                           {viewMode === 'week' && (
                             <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                               {getSiteName(task.site_id)}
                             </div>
                           )}
                         </div>
                      
                                             {/* Тултип */}
                       <div className="task-tooltip fixed w-80 bg-gray-900 text-white text-xs rounded-lg py-2 px-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 shadow-xl"
                            style={{
                              zIndex: 99999,
                              bottom: 'auto',
                              top: 'auto',
                              left: 'auto',
                              right: 'auto'
                            }}>
                        <div className="font-semibold mb-1">{task.name}</div>
                        <div className="space-y-1 text-gray-300">
                          <div className="flex items-center space-x-2">

                        {isTaskEditor(task) ? (
                          <div className="mt-2">
                            <StatusDropdown
                              task={task}
                              openStatusDropdowns={openStatusDropdowns}
                              updatingStatus={updatingStatus}
                              handleStatusChange={handleStatusChange}
                              closeStatusDropdown={closeStatusDropdown}
                              currentLang={currentLang}
                            />
                          </div>
                        ) :
                            <span className={`px-2 py-1 rounded text-xs ${getStatusColor(task.status)}`}>
                              {getStatusText(task.status)}
                            </span>
                        }
                            {isTaskOverdue(task) && (
                              <span className="text-red-400">
                                {getDaysOverdue(task.d_end)} {currentLang === 'ru' ? 'дн. просрочено' : 'days overdue'}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center space-x-1">
                            <div
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: getSiteColor(task.site_id) }}
                            ></div>
                            <span>{getSiteName(task.site_id)}</span>
                          </div>
                                                     <div className="flex items-center space-x-2">
                             <span>{formatDate(task.d_start)} - {formatDate(task.d_end)}</span>
                             <span>{task.pct || 0}%</span>
                           </div>
                          {task.text && (
                            <div className="line-clamp-2">
                              {stripHtml(task.text)}
                            </div>
                          )}
                        </div>
                        
                        {/* Действия в тултипе */}
                        <div className="flex items-center space-x-2 mt-2 pt-2 border-t border-gray-700">
                          <Link
                            to={`/seo/tasks/${task.id}`}
                            className="text-blue-400 hover:text-blue-300 text-xs"
                          >
                            {currentLang === 'ru' ? 'Просмотр' : 'View'}
                          </Link>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onMessagesClick(task.id);
                            }}
                            className="text-blue-400 hover:text-blue-300 text-xs"
                          >
                            {currentLang === 'ru' ? 'Комментарии' : 'Comments'}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onFilesClick(task.id);
                            }}
                            className="text-green-400 hover:text-green-300 text-xs"
                          >
                            {currentLang === 'ru' ? 'Файлы' : 'Files'}
                          </button>
                        </div>
                        
                        
                                                 {/* Стрелка тултипа */}
                         <div className="absolute w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"
                              style={{
                                top: '100%',
                                left: '50%',
                                transform: 'translateX(-50%)'
                              }}></div>
                       </div>
                     </div>
                   );
                 })}
                  
                                     {/* Показать больше задач (только в месячном режиме) */}
                   {viewMode === 'month' && dayTasks.length > 3 && (
                     <div className="text-xs text-gray-500 dark:text-gray-400 text-center py-1">
                       +{dayTasks.length - 3} {currentLang === 'ru' ? 'еще' : 'more'}
                     </div>
                   )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Легенда */}
      <div className="bg-white rounded-lg shadow-md p-4 dark:bg-navy-700 dark:text-white dark:border-white/10">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          {currentLang === 'ru' ? 'Легенда' : 'Legend'}
        </h3>
                 <div className="flex flex-wrap gap-4">
           <div className="flex items-center space-x-2">
             <div className="w-4 h-4 bg-white border-l-8 border-yellow-500 rounded"></div>
             <span className="text-sm">{currentLang === 'ru' ? 'Ожидает' : 'Pending'}</span>
           </div>
           <div className="flex items-center space-x-2">
             <div className="w-4 h-4 bg-white border-l-8 border-blue-500 rounded"></div>
             <span className="text-sm">{currentLang === 'ru' ? 'В работе' : 'In Progress'}</span>
           </div>
           <div className="flex items-center space-x-2">
             <div className="w-4 h-4 bg-white border-l-8 border-green-500 rounded"></div>
             <span className="text-sm">{currentLang === 'ru' ? 'Завершена' : 'Completed'}</span>
           </div>
           <div className="flex items-center space-x-2">
             <div className="w-4 h-4 bg-white border-l-8 border-red-500 rounded"></div>
             <span className="text-sm">{currentLang === 'ru' ? 'Просрочена' : 'Overdue'}</span>
           </div>
           <div className="flex items-center space-x-2">
             <div className="w-4 h-4 bg-white border-l-8 border-gray-500 rounded"></div>
             <span className="text-sm">{currentLang === 'ru' ? 'Отменена' : 'Cancelled'}</span>
           </div>
         </div>
      </div>

      {/* Пустое состояние */}
      {tasks.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 dark:bg-gray-700">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <h4 className="text-lg font-medium text-gray-900 mb-2 dark:text-white">
            {currentLang === 'ru' ? 'Задачи не найдены' : 'No tasks found'}
          </h4>
          <p className="text-gray-500 dark:text-gray-400">
            {currentLang === 'ru' 
              ? 'Попробуйте изменить фильтры или создать новую задачу' 
              : 'Try changing filters or create a new task'
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default TimelineView; 